
// Base.
import { Prop, Mixins } from 'vue-property-decorator'
import Component from 'vue-class-component'
import usersMixins from './UsersMixin'
// Used for notifications.
import UserService from '@/services/UserService'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import BlueButton from '../Atomic/BlueButton.vue'
import GreenButton from '../Atomic/GreenButton.vue'
import RedButton from '../Atomic/RedButton.vue'
import CardTitle from '@/components/Atomic/CardTitle.vue'
// Used Models.
import { User } from '@/models/user'
import ProgressLinear from '@/components/Atomic/ProgressLinear.vue'

import XLSX from 'xlsx'
import ToolTipBlueButton from '@/components/Atomic/ToolTipButton.vue'

interface StudentData {
  ad_personne_id: number
  password: string
}

@Component({
  components: {
    ToolTipBlueButton,
    ProgressLinear,
    BlueButton,
    RedButton,
    GreenButton,
    CardTitle,
  },
})
export default class CsvExport extends Mixins(usersMixins) {
  private errorHandlerService = new ErrorHandlerService()
  // Used for CSV Export.
  private jsonMeta: any = [{ key: 'charset', value: 'utf-8' }]
  private jsonFieldsWithPassword: any = {}

  @Prop() private readonly schoolID!: string
  @Prop() private readonly getJsonData!: User[]
  @Prop() private readonly users!: User[]
  @Prop() private mainWindowPending: boolean
  @Prop() private iconSize?: number

  private needActivationKey: boolean = false
  private studentsToUpdate: any = []
  private exportToExcel: boolean = true
  private formatSelection: Array<{text: string, value: boolean}> = [{text: 'Excel (XLSX)', value: true}, {text: 'CSV', value: false}]
  private excelHeaders: {} = {}

  private get hasNoUsersOrPending() {
    if (!this.isPending) {
      return this.users.length === 0
    }
    return false
  }

  public mounted() {
    this.initialize()
  }

  private initialize() {
    this.setCsvExportConfig()
    this.setExcelExportConfig()
  }

  private setCsvExportConfig() {
    this.jsonFieldsWithPassword[this.$t('Establishment').toString()] =
      'etablissement_nom'
    this.jsonFieldsWithPassword[this.$t('Name').toString()] = 'nom'
    this.jsonFieldsWithPassword[this.$t('FirstName').toString()] = 'prenom'
    this.jsonFieldsWithPassword[this.$t('Username').toString()] = 'identifiant'
    this.jsonFieldsWithPassword[this.$t('Office').toString()] = 'courriel'
    this.jsonFieldsWithPassword[this.$t('Function').toString()] = 'fonctions'
    this.jsonFieldsWithPassword[this.$t('USER.Grade').toString()] = 'classes'
    this.jsonFieldsWithPassword[this.$t('USER.wifiCSV').toString()] = 'wifiCSV'
    this.jsonFieldsWithPassword[this.$t('USER.Password').toString()] =
      'password'
  }

  private setExcelExportConfig() {
    this.excelHeaders['etablissement_nom'] = this.$t('Establishment').toString()
    this.excelHeaders['nom'] = this.$t('Name').toString()
    this.excelHeaders['prenom'] = this.$t('FirstName').toString()
    this.excelHeaders['identifiant'] = this.$t('Username').toString()
    this.excelHeaders['courriel'] = this.$t('Office').toString()
    this.excelHeaders['fonctions'] = this.$t('Function').toString()
    this.excelHeaders['classes'] = this.$t('USER.Grade').toString()
    this.excelHeaders['wifiCSV'] = this.$t('USER.wifiCSV').toString()
    this.excelHeaders['password'] = this.$t('USER.Password').toString()
  }

  private async exportToXls() {
    await this.retrieveDefaultPassword()
    const workbook = XLSX.utils.book_new()
    const usersToExport = await this.changeUserAttrForExport()
    const sheetData = XLSX.utils.json_to_sheet(usersToExport, {header: Object.keys(this.jsonFieldsWithPassword)})
    XLSX.utils.book_append_sheet(workbook, sheetData)
    this.needActivationKey = false
    XLSX.writeFile(workbook, 'utilisateurs.xlsx')
    this.dialog = false
  }

  private async changeUserAttrForExport() {
    let users
    if (this.getJsonData.length === 0) {
      users = this.users.map((user) => Object.assign({}, user))
    } else {
      users = this.getJsonData.map((user) => Object.assign({}, user))
    }
    users.map( (user: User) => {
      if (user['password'] === undefined) {
        user['password'] = ''
      }
      Object.keys(user).forEach((key) => {
          if (this.excelHeaders[key] !== undefined) {
            if (key === 'classes') {
              if (Array.isArray(user[key]) && user[key].length > 0) {
                user[this.excelHeaders[key]] = user[key].join()
              }
            } else {
                user[this.excelHeaders[key]] = user[key]
            }
          }
          delete user[key]
      })
    })
    return users
  }

  private async retrieveDefaultPassword() {
    this.isPending = true
    if (this.needActivationKey) {
      const students: number[] = []
      const dataToUpdate =
        this.getJsonData.length >= 1 ? this.getJsonData : this.users
      try {
        dataToUpdate.forEach((user) => {
          if (user.fonctions === 'Élève' && !user.password) {
            students.push(user.ad_personne_id)
          }
        })
        if (students.length >= 1) {
          if (this.getJsonData.length >= 200) {
            if (this.schoolID !== '') {
              this.studentsToUpdate = await UserService.getDefaultStudentsPasswordsForSchool(
                  this.schoolID,
              )
            }
          } else {

            this.studentsToUpdate = await UserService.getDefaultStudentsPasswords(
                students,
            )
          }
          this.studentsToUpdate.forEach((student: StudentData) => {
            const studentData = dataToUpdate.find(
              (user) => user.ad_personne_id === student.ad_personne_id,
            )
            if (studentData !== undefined) {
              studentData.password = student.password
            }
          })
        }
      } catch (error: any) {
        this.errorHandlerService.handle(this, error.response.data.code, true)
      } finally {
        this.studentsToUpdate = []
        this.needActivationKey = false
        this.exportToExcel = true
        this.dialog = false
      }
    }
    this.isPending = false
  }

  private abort() {
    this.exportToExcel = true
    this.needActivationKey = false
    this.cancel()
  }
}
